import { GAMES, type GameId } from '@ezishizi/learning-core';
import { GameGlyph } from './GameGlyph.tsx';

/** One line under a game's name in the chooser: what you do, never what the answer is. */
const HINT: Partial<Record<GameId, string>> = {
  bubbles: 'Pop the right bubbles',
  forks: 'Steer the boat',
  trio: 'Hop the lily pads',
  feed: 'Feed Shizi what he asks for',
  find_all: 'Spot every one',
  match_pairs: 'Match the pairs',
  sort_meaning: 'Sort into baskets',
  count: 'Count and listen',
  tone_paint: 'Paint the tone',
  tone_slide: 'Slide along the tone',
  sort_sound: 'Sort the shells',
  reveal: 'Write to reveal the picture',
  flash: 'Look, then write',
  stroke_order: 'Strokes in order',
  stroke_missing: 'Draw the missing stroke',
  bridge: 'Build the bridge',
  scene: 'Pick the right scene',
  build: 'Listen, then build',
  builder: 'Put the character together',
};

const nameOf = (g: GameId) => GAMES.find((x) => x.id === g)?.label ?? g;

/**
 * "Pick your game": the stop that offers two formats asks which to play before it opens. Two big
 * cards with a picture, a name and one line each; nothing scrolls, and the cards sit side by side
 * at every screen shape (they are sized in dvh so a short laptop screen still fits them).
 */
export function GameChooser({
  games,
  onPick,
  onClose,
}: {
  games: readonly [GameId, GameId];
  onPick: (game: GameId) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-ink/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Pick your game"
      data-testid="game-chooser"
      onClick={onClose}
    >
      <div
        className="pop relative flex max-h-full w-full max-w-2xl flex-col items-center gap-[2dvh] rounded-[32px] bg-paper p-[3dvh] text-center shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn btn-ghost absolute top-3 right-3 h-12 min-h-0 w-12 min-w-0 p-0 text-xl"
          aria-label="Close"
          data-testid="choose-close"
          onClick={onClose}
        >
          ✕
        </button>
        <div className="display text-[clamp(1.5rem,4dvh,2.25rem)] text-brand">Pick your game</div>
        <div className="grid w-full grid-cols-2 gap-[2dvh]">
          {games.map((g) => (
            <button
              key={g}
              type="button"
              className="pressable flex flex-col items-center justify-center gap-[1dvh] rounded-3xl bg-white px-3 py-[2.5dvh] shadow-md"
              data-testid={`choose-${g}`}
              onClick={() => onPick(g)}
            >
              <GameGlyph game={g} className="h-[min(14dvh,7rem)] w-[min(14dvh,7rem)]" />
              <span className="text-[clamp(1.1rem,2.6dvh,1.5rem)] leading-tight font-bold">
                {nameOf(g)}
              </span>
              <span className="text-[clamp(0.85rem,2dvh,1.1rem)] leading-tight text-muted">
                {HINT[g] ?? ''}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
